/**
 * AdminMarket — Phase 9 RentCast usage + cache dashboard.
 *
 * Admin-only view:
 *   - KPI strip: calls this month / 50 limit · active cache entries · last call
 *   - Recent calls table (latest 25): endpoint, status, latency, called_at
 *   - Cache contents table: cache_key, endpoint, fetched_at, expires_at, [Invalidate]
 *
 * Read-only except the per-row "Invalidate" button on cache entries.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExternalLink, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

function fmtTs(s) {
  if (!s) return "—";
  return new Date(s).toLocaleString();
}

function statusBadge(code) {
  if (code === 200) return <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-800">200</span>;
  if (code === -1) return <span className="rounded-md bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">network</span>;
  if (code >= 400) return <span className="rounded-md bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">{code}</span>;
  return <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700">{code ?? "—"}</span>;
}

export default function AdminMarket() {
  const [usage, setUsage] = useState(null);
  const [attomUsage, setAttomUsage] = useState(null);
  const [calls, setCalls] = useState(null);
  const [cache, setCache] = useState(null);
  const [discoveryQuota, setDiscoveryQuota] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, au, c, k, dq] = await Promise.all([
        api.get("/market/usage"),
        api.get("/market/attom/admin/usage").catch(() => ({ data: null })),
        api.get("/market/admin/calls", { params: { limit: 25 } }),
        api.get("/market/admin/cache", { params: { limit: 100 } }),
        api.get("/market/admin/discovery/quota-history").catch(() => ({ data: null })),
      ]);
      setUsage(u.data);
      setAttomUsage(au.data);
      setCalls(c.data);
      setCache(k.data);
      setDiscoveryQuota(dq.data);
    } catch (e) {
      toast.error("Failed to load market usage");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function invalidate(key) {
    if (!window.confirm("Invalidate this cache entry? Next request will hit RentCast again.")) return;
    try {
      await api.delete(`/market/admin/cache/${encodeURIComponent(key)}`);
      toast.success("Cache entry invalidated");
      load();
    } catch (e) {
      toast.error("Failed to invalidate");
    }
  }

  if (!usage) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="admin-market-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading RentCast usage…
      </div>
    );
  }

  const used = usage.calls_this_month ?? 0;
  const limit = usage.free_tier_limit ?? 50;
  const usePct = Math.min(100, Math.round((used / limit) * 100));
  const usageTone =
    used >= (usage.free_tier_safe_cap ?? 45)
      ? "bg-red-50 border-red-200 text-red-900"
      : used >= limit * 0.6
      ? "bg-amber-50 border-amber-200 text-amber-900"
      : "bg-emerald-50 border-emerald-200 text-emerald-900";

  return (
    <div className="space-y-6 fade-in" data-testid="admin-market">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Integrations</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">RentCast Usage</h1>
          <p className="mt-1 text-sm text-slate-500">Monthly call budget · cache health · audit trail</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-market-refresh">
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className={`rounded-xl border p-4 ${usageTone}`} data-testid="kpi-market-calls">
          <p className="text-xs font-medium uppercase tracking-wide opacity-70">RentCast · calls this month</p>
          <p className="mt-1 text-2xl font-semibold">{used} / {limit}</p>
          <div className="mt-2 h-1.5 w-full rounded-full bg-white/60">
            <div
              className="h-1.5 rounded-full bg-current opacity-70"
              style={{ width: `${usePct}%` }}
            />
          </div>
          <p className="mt-2 text-xs opacity-70">
            Safe cap at {usage.free_tier_safe_cap}{usage.free_tier ? " · free tier" : ""}
          </p>
        </div>
        {attomUsage?.enabled === false ? (
          <div
            className="rounded-xl border border-slate-200 bg-slate-50 p-4"
            data-testid="kpi-attom-disabled"
            title="ATTOM subscription canceled. Set ATTOM_ENABLED=true and renew the API key to re-enable."
          >
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">ATTOM</p>
            <p className="mt-1 text-base font-semibold text-slate-700">Disabled</p>
            <p className="mt-1 text-xs text-slate-500">
              {attomUsage.disabled_reason === "subscription_canceled"
                ? "Subscription canceled · "
                : ""}
              <a
                href="https://api.developer.attomdata.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-700 hover:underline"
              >
                Re-enable when renewed →
              </a>
            </p>
          </div>
        ) : attomUsage ? (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-indigo-900" data-testid="kpi-attom-calls">
            <p className="text-xs font-medium uppercase tracking-wide opacity-70">ATTOM · calls this month</p>
            <p className="mt-1 text-2xl font-semibold">{attomUsage.calls_this_month}</p>
            <p className="mt-2 text-xs opacity-70">
              ≈ ${attomUsage.estimated_cost_usd?.toFixed?.(2) ?? "0.00"} estimated cost
              {" · hit rate "}
              {attomUsage.cache_hit_rate === null ? "—" : `${Math.round((attomUsage.cache_hit_rate || 0) * 100)}%`}
            </p>
            <p className="mt-1 text-xs opacity-70">
              {attomUsage.cache_active_entries} active cache · {attomUsage.cache_entries} total
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="kpi-attom-unavailable">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">ATTOM</p>
            <p className="mt-1 text-sm text-slate-700">Status unavailable</p>
          </div>
        )}
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="kpi-market-cache">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">RentCast cache</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{usage.cache_active_entries}</p>
          <p className="mt-2 text-xs text-slate-500">{usage.cache_entries} total · {usage.cache_hit_rate === null ? "—" : `${Math.round(usage.cache_hit_rate * 100)}% hit rate`}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="kpi-market-last">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Last RentCast call</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{fmtTs(usage.last_call?.called_at)}</p>
          <p className="mt-1 text-xs text-slate-500">
            {usage.last_call ? `${usage.last_call.endpoint} · status ${usage.last_call.status_code}` : "no calls yet"}
          </p>
        </div>
      </div>

      {/* Phase 36 Batch AN — Discovery Quota Status (with 7-day usage chart). */}
      {discoveryQuota && (
        <section
          className="rounded-xl border border-slate-200 bg-white shadow-sm"
          data-testid="discovery-quota-section"
        >
          <header className="flex flex-col gap-2 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Discovery Quota Status</h2>
              <p className="text-xs text-slate-500">
                RentCast call budget powering Property Discovery digests.
              </p>
            </div>
            <a
              href={discoveryQuota.upgrade_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="discovery-quota-upgrade-link"
              className="inline-flex items-center gap-1.5 self-start rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 transition-colors hover:bg-indigo-100"
            >
              Upgrade RentCast plan
              <ExternalLink className="h-3 w-3" />
            </a>
          </header>

          <div className="grid gap-4 px-4 py-4 lg:grid-cols-2">
            {/* Headline + progress + sub-stats */}
            <div data-testid="discovery-quota-summary" className="space-y-3">
              <div className="flex items-baseline justify-between">
                <p className="text-2xl font-semibold tabular-nums text-slate-900">
                  {discoveryQuota.used_calls} / {discoveryQuota.monthly_limit}
                  <span className="ml-1 text-sm font-normal text-slate-500">calls this month</span>
                </p>
                {discoveryQuota.low && (
                  <span
                    data-testid="discovery-quota-low-badge"
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      discoveryQuota.exhausted
                        ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {discoveryQuota.exhausted ? "Exhausted" : "Low quota"}
                  </span>
                )}
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  data-testid="discovery-quota-progress"
                  className={`h-full rounded-full transition-all ${
                    discoveryQuota.use_pct >= 95
                      ? "bg-rose-500"
                      : discoveryQuota.use_pct >= 75
                        ? "bg-amber-500"
                        : "bg-emerald-500"
                  }`}
                  style={{ width: `${Math.min(100, discoveryQuota.use_pct)}%` }}
                />
              </div>
              <dl className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <dt className="text-slate-500">Remaining</dt>
                  <dd className="font-semibold text-slate-900 tabular-nums">
                    {discoveryQuota.remaining_calls}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Resets</dt>
                  <dd className="font-semibold text-slate-900">
                    {new Date(discoveryQuota.resets_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Low threshold</dt>
                  <dd className="font-semibold text-slate-900 tabular-nums">
                    {discoveryQuota.low_threshold} calls
                  </dd>
                </div>
              </dl>
            </div>

            {/* 7-day usage bar chart */}
            <div data-testid="discovery-quota-chart" className="h-40 w-full">
              <p className="mb-1 text-xs font-medium text-slate-500">
                Last 7 days · daily live calls
              </p>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={(discoveryQuota.history || []).map((b) => ({
                    label: new Date(b.date).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    }),
                    calls: b.calls,
                  }))}
                  margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748b" }} width={28} />
                  <RechartsTooltip
                    cursor={{ fill: "#f1f5f9" }}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Bar dataKey="calls" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      {/* Recent calls */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <header className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Recent calls</h2>
          <p className="text-xs text-slate-500">{calls?.length || 0} rows</p>
        </header>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Endpoint</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Latency</TableHead>
                <TableHead>Called at</TableHead>
                <TableHead>Inputs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(calls || []).map((c, i) => (
                <TableRow key={`${c.called_at}-${i}`}>
                  <TableCell className="font-medium">{c.endpoint}</TableCell>
                  <TableCell>{statusBadge(c.status_code)}</TableCell>
                  <TableCell className="text-right tabular-nums">{c.latency_ms} ms</TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtTs(c.called_at)}</TableCell>
                  <TableCell className="max-w-md truncate text-xs text-slate-500">
                    {Object.entries(c.inputs || {}).map(([k, v]) => `${k}=${v}`).join(" · ")}
                  </TableCell>
                </TableRow>
              ))}
              {(!calls || calls.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-slate-500">No calls yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Cache contents */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <header className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Cache entries</h2>
          <p className="text-xs text-slate-500">{cache?.length || 0} rows (newest first, 30-day TTL)</p>
        </header>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Endpoint</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Inputs</TableHead>
                <TableHead>Fetched</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(cache || []).map((row) => (
                <TableRow key={row.cache_key}>
                  <TableCell className="font-medium">{row.endpoint}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{row.cache_key.slice(0, 10)}…</TableCell>
                  <TableCell className="max-w-xs truncate text-xs text-slate-500">
                    {Object.entries(row.inputs || {}).map(([k, v]) => `${k}=${v}`).join(" · ")}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtTs(row.fetched_at)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtTs(row.expires_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => invalidate(row.cache_key)}
                      data-testid={`invalidate-${row.cache_key.slice(0, 8)}`}
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      Invalidate
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {(!cache || cache.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-slate-500">
                    No cache entries yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
